// src/components/Dashboard/components/LoadingStates.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, AlertCircle, RefreshCw } from 'lucide-react';
import StockerEyeLoader from '../../../components/loader/StockerEyeLoader';

/**
 * Reusable loading state components for the dashboard
 */

// Full Page Loading
export const FullPageLoading = ({ message }) => {
  const { t } = useTranslation();
  const displayMessage = message ?? t('dashboard.loadingStates.loading');
  return (
    <div className="flex-1 flex items-center justify-center py-24 bg-gradient-to-br from-gray-50 via-gray-50 to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900">
      <div className="flex flex-col items-center gap-4">
        <StockerEyeLoader size={64} />
        {displayMessage && (
          <p className="text-gray-500 dark:text-slate-400 text-sm text-center">{displayMessage}</p>
        )}
      </div>
    </div>
  );
};

// Card Loading Skeleton
export const CardLoadingSkeleton = ({ count = 3 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div 
        key={i}
        className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-4 animate-pulse"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="space-y-2">
            <div className="h-5 w-20 bg-slate-700/50 rounded"></div>
            <div className="h-3 w-32 bg-slate-700/30 rounded"></div>
          </div>
          <div className="h-8 w-24 bg-slate-700/50 rounded"></div>
        </div>
        <div className="h-10 bg-slate-700/30 rounded mb-3"></div>
        <div className="space-y-2">
          <div className="h-2 bg-slate-700/50 rounded"></div>
          <div className="h-2 bg-slate-700/30 rounded w-3/4"></div>
        </div>
      </div>
    ))}
  </div>
);

// Inline Loading Spinner
export const InlineLoading = ({ size = 'md', message }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`animate-spin rounded-full border-2 border-blue-600 border-t-transparent ${sizeClasses[size]}`}></div>
      {message && <span className="text-sm text-gray-500 dark:text-slate-400">{message}</span>}
    </div>
  );
};

// Data Loading State
export const DataLoading = ({ message }) => {
  const { t } = useTranslation();
  const displayMessage = message ?? t('dashboard.loadingStates.loadingData');
  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <div className="relative inline-block">
          <Activity className="w-8 h-8 text-blue-500 animate-pulse" />
          <div className="absolute inset-0 animate-ping">
            <Activity className="w-8 h-8 text-blue-400 opacity-20" />
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-3">{displayMessage}</p>
      </div>
    </div>
  );
};

// Error State
export const ErrorState = ({
  title,
  message,
  onRetry
}) => {
  const { t } = useTranslation();
  const displayTitle = title ?? t('dashboard.loadingStates.errorTitle');
  const displayMessage = message ?? t('dashboard.loadingStates.errorMessage');
  return (
    <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-xl p-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">{displayTitle}</h3>
          <p className="text-xs text-red-600/80 dark:text-red-400/80 mb-3">{displayMessage}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-600/20 dark:hover:bg-red-600/30 border border-red-300 dark:border-red-500/30 rounded-lg text-xs text-red-700 dark:text-red-300 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>{t('dashboard.loadingStates.tryAgain')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Empty State
export const EmptyState = ({
  icon: Icon = Activity,
  title,
  message,
  action,
  actionLabel
}) => {
  const { t } = useTranslation();
  const displayTitle = title ?? t('dashboard.loadingStates.emptyTitle');
  const displayMessage = message ?? t('dashboard.loadingStates.emptyMessage');
  return (
    <div className="bg-cream-100 dark:bg-slate-900/40 border border-cream-300 dark:border-slate-800/40 rounded-xl p-8 text-center">
      <Icon className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3 opacity-50" />
      <h3 className="text-base font-semibold text-gray-900 dark:text-slate-300 mb-2">{displayTitle}</h3>
      <p className="text-sm text-gray-500 dark:text-slate-500 mb-4 max-w-md mx-auto">{displayMessage}</p>
      {action && actionLabel && (
        <button
          onClick={action}
          className="px-4 py-2 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white text-sm rounded-lg font-medium transition-colors"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

// Shimmer Effect for Loading Lists
export const ListLoadingSkeleton = ({ count = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div 
        key={i}
        className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-4 animate-pulse"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-slate-700/50 rounded-full flex-shrink-0"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-slate-700/50 rounded w-1/4"></div>
            <div className="h-3 bg-slate-700/30 rounded w-3/4"></div>
            <div className="h-3 bg-slate-700/30 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Pulsing Dot Indicator
export const PulsingDot = ({ color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500'
  };

  return (
    <div className="relative inline-flex">
      <div className={`w-2 h-2 ${colorClasses[color]} rounded-full animate-pulse`}></div>
      <div className={`absolute inset-0 w-2 h-2 ${colorClasses[color]} rounded-full animate-ping opacity-75`}></div>
    </div>
  );
};

// Progress Bar
export const ProgressBar = ({ progress, label, showPercentage = true }) => (
  <div className="space-y-1">
    {label && (
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500 dark:text-slate-400">{label}</span>
        {showPercentage && (
          <span className="text-gray-700 dark:text-slate-300 font-medium">{progress}%</span>
        )}
      </div>
    )}
    <div className="h-2 bg-gray-200 dark:bg-slate-800/60 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-blue-600 to-blue-500 transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  </div>
);

export default {
  FullPageLoading,
  CardLoadingSkeleton,
  InlineLoading,
  DataLoading,
  ErrorState,
  EmptyState,
  ListLoadingSkeleton,
  PulsingDot,
  ProgressBar
};